import React, {memo} from 'react';
import {t} from '@lingui/macro';
import {useLingui} from "@lingui/react";
import {Select} from "../../DataEntry";
import {Type} from '../../../redux/Transaction/helper';
import {getTypeName} from '../../TransactionType';
import {TransactionFilterKey} from "../../FilterForm/Transaction/TransactionFilterKey";
import styled, {css} from 'styled-components';
import { AccessHelper } from '../../Access/helper/helper';
import { SubscriptionCheck } from '../../Access/helper/subscription/SubscriptionCheck';

const { Option, OptGroup } = Select;

const stockTransactionTypes = new Set([
    Type.stock_adjustment,
    Type.stock_transfer,
]);

const excludeTransactionTypes = (groups, excludedTypes) => groups.reduce((result, group) => {
    const children = group.children.filter(type => !excludedTypes.has(type));

    if (children.length === 0)
        return result;

    result.push(
        children.length === group.children.length
            ? group
            : { ...group, children },
    );
    return result;
}, []);

const StyledSelect = styled(Select)`
  .ant-select-selector {
    .toggle-short-long-name {
      .short-name {
        /* display: none; */
      }
    }
  }
`;

export const StyledSelectDropdownCss = css`
  .ant-select-item-option-content {
    .toggle-short-long-name {
      .long-name {
        /* display: none; */
      }
    }
  }
`;

/**
 * Options
 * - onChange
 * - filteredTypes
 * - restProps injected by Form.Item or user-defined
 **/
function TransactionTypeSelect(props) {
    const {
        // Parent
        onChange,
        filteredTypes,
        inputRef,
        checkSubscription,
        style,
        // placeholder,
        ...restProps
    } = props;

    useLingui();

    const getAllTypeGroups = () => [
        {
            label: t`Sales`,
            children: [
                Type.sale_quote,
                Type.sale_order,
                Type.sale_delivery_order,
                Type.sale_invoice,
                Type.sale_credit_note,
                Type.sale_payment,
                Type.sale_refund,
            ]},
        {
            label: t`Purchases`,
            children: [
                Type.purchase_order,
                Type.purchase_goods_received_note,
                Type.purchase_bill,
                Type.purchase_credit_note,
                Type.purchase_payment,
                Type.purchase_refund,
            ],
        },
        {
            label: t`Bank`,
            children: [
                Type.bank_income,
                Type.bank_expense,
                Type.bank_transfer,
            ],
        },
        {
            label: t`Stock`,
            children: [
                Type.stock_adjustment,
                Type.stock_transfer,
            ],
        },
        {
            label: t`Accounting`,
            children: [
                Type.journal_entry,
                Type.contra,
                Type.tax_deemed_payment,
            ],
        },
    ];

    const getOptions = () => {
        let types = [];
        let groups = [];

        switch (filteredTypes) {
            case TransactionFilterKey.instruction_type:
                // types = [
                //     Type.sale_invoice, Type.purchase_bill,
                //     Type.bank_income, Type.bank_expense, Type.journal_entry,
                // ];
                groups = [
                    { label: t`Sales`, children: [Type.sale_invoice]},
                    { label: t`Purchases`, children: [Type.purchase_bill]},
                    { label: t`Bank`, children: [Type.bank_income, Type.bank_expense, Type.bank_transfer]},
                    { label: t`Accounting`, children: [Type.journal_entry]},
                ];
                break;
            case TransactionFilterKey.sale_invoice_type:
                types = [Type.sale_invoice, Type.sale_credit_note];
                break;
            case TransactionFilterKey.sale_payment_type:
                types = [Type.sale_payment, Type.sale_refund];
                break;
            case TransactionFilterKey.purchase_bill_type:
                types = [Type.purchase_bill, Type.purchase_credit_note];
                break;
            case TransactionFilterKey.purchase_payment_type:
                types = [Type.purchase_payment, Type.purchase_refund];
                break;
            case 'manual_reconciliation':
                types = [
                    Type.sale_invoice, Type.sale_credit_note, Type.sale_payment, Type.sale_refund,
                    Type.purchase_bill, Type.purchase_credit_note,
                    Type.purchase_payment, Type.purchase_refund,
                    Type.bank_income, Type.bank_expense, Type.bank_transfer, Type.journal_entry,
                ];
                break;
            case TransactionFilterKey.profit_summary:
                types = [Type.sale_invoice, Type.sale_delivery_order];
                break;
            case TransactionFilterKey.official_receipt_type:
                types = [Type.sale_payment, Type.purchase_refund, Type.bank_income];
                break;
            case TransactionFilterKey.payment_voucher_type:
                types = [Type.sale_refund, Type.purchase_payment, Type.bank_expense];
                break;

            case TransactionFilterKey.all_types:
                groups = getAllTypeGroups();
                break;
            case TransactionFilterKey.transaction_list_report_type:
                groups = excludeTransactionTypes(getAllTypeGroups(), stockTransactionTypes);
                break;
            case TransactionFilterKey.double_entry_type:
                groups = [
                    { label: t`Sales`,
                        children: [
                            // Type.sale_quote, Type.sale_order,
                            Type.sale_delivery_order,
                            Type.sale_invoice, Type.sale_credit_note, Type.sale_payment, Type.sale_refund,
                        ]},
                    {
                        label: t`Purchases`,
                        children: [
                            // Type.purchase_order,
                            Type.purchase_goods_received_note, Type.purchase_bill,
                            Type.purchase_credit_note, Type.purchase_payment, Type.purchase_refund,
                        ],
                    },
                    { label: t`Bank`, children: [Type.bank_income, Type.bank_expense, Type.bank_transfer]},
                    { label: t`Stock`, children: [Type.stock_adjustment, Type.stock_transfer]},
                    {
                        label: t`Accounting`,
                        children: [Type.journal_entry, Type.contra, Type.tax_deemed_payment],
                    },
                ];
                break;
            case TransactionFilterKey.number_format: {
                groups = [
                    {
                        label: t`Sales`,
                        children: [
                            Type.sale_quote,
                            Type.sale_order,
                            Type.sale_delivery_order,
                            Type.sale_invoice,
                            Type.sale_credit_note,
                        ],
                    },
                    {
                        label: t`Purchases`,
                        children: [
                            Type.purchase_order,
                            Type.purchase_goods_received_note,
                            Type.purchase_bill,
                            Type.purchase_credit_note,
                        ],
                    },
                    { label: t`Payments`,
                        children: [
                            Type.official_receipt,
                            Type.payment_voucher,
                        ],
                    },
                    {
                        label: t`Bank`,
                        children: [
                            Type.bank_transfer,
                        ],
                    },
                    {
                        label: t`Stocks`,
                        children: [
                            Type.stock_adjustment,
                            Type.stock_transfer,
                        ],
                    },
                    {
                        label: t`Accounting`,
                        children: [
                            Type.journal_entry,
                            Type.contra,
                            Type.tax_deemed_payment,
                        ],
                    },
                    {
                        label: t`Others`,
                        children: [
                            Type.fixed_asset,
                        ],
                    },
                ];
                break;
            }
            default:
                throw Error(`No such filteredTypes ${filteredTypes}`);
        }
        return {types, groups};
    };

    const renderOptions = (types, groups) => {
        if (types.length) {
            return types.map(type => {
                const hasSubscription = checkSubscription ?
                    SubscriptionCheck.withTransactionType(type).hasSubscriptionTo() :
                    true;
                const label = AccessHelper.getFormLabel(getTypeName(type)[1], !hasSubscription);
                return (
                    <Option
                        key={type}
                        value={type}
                        longName={getTypeName(type, false)[1]}
                        disabled={!hasSubscription}
                    >
                        {label}
                    </Option>
                );
            },
            );
        }
        if (groups.length) {
            return groups.map(group => (
                <OptGroup label={group.label} key={group.label}>
                    { group.children.map(type => {
                        const hasSubscription = checkSubscription ?
                            SubscriptionCheck.withTransactionType(type).hasSubscriptionTo() :
                            true;
                        const label = AccessHelper.getFormLabel(getTypeName(type, true)[1], !hasSubscription);
                        return (
                            <Option
                                key={type}
                                value={type}
                                disabled={!hasSubscription}
                                label={`${group.label} ${getTypeName(type, false)[1]} ${getTypeName(type, true)[1]}`}
                                longName={getTypeName(type, false)[1]}
                            >
                                <span className="toggle-short-long-name">
                                    <span className="short-name">{label}</span>
                                    {/* <span className="long-name">{getTypeName(type, false)[1]}</span> */}
                                </span>
                            </Option>

                        );
                    },
                    ) }
                </OptGroup>
            ));
        }
        return null;
    };

    const {types, groups} = getOptions();

    const selectProps = {
        ...groups.length ? { optionFilterProp: "label" } : {},
    };

    return (
        <StyledSelect
            placeholder={t`All`}
            onChange={onChange}
            allowClear
            showSearch
            dropdownMatchSelectWidth={false}
            optionFilterProp="label"
            optionLabelProp="longName"
            // optionLabelProp="children"
            ref={inputRef}
            style={style}
            {...selectProps}
            {...restProps}
        >
            { renderOptions(types, groups) }
        </StyledSelect>
    );
}

export default memo(TransactionTypeSelect);
